import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type AdminWorkspace } from '../../api';
import { useData } from '../../useData';
import { ErrorMsg, Modal } from '../../components/Modal';
import { WorkspaceForm } from '../../components/WorkspaceForm';
import { ThemePicker } from '../../components/ThemePicker';

const PRIVACY: Record<string, string> = { Open: 'Deschis', Closed: 'Închis', Private: 'Privat' };

// Administrare → Setări. Deocamdată: workspace-urile (creare, editare, ordine, arhivare/restaurare).
export function SettingsPage({ onChanged }: { onChanged: () => void }) {
  const { data, error, reload } = useData<AdminWorkspace[]>('/admin/workspaces');
  const [editing, setEditing] = useState<AdminWorkspace | 'new' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AdminWorkspace | null>(null);

  const active = (data ?? []).filter((w) => !w.isArchived);
  const archived = (data ?? []).filter((w) => w.isArchived);

  async function run(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
      reload();
      onChanged();
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  function move(index: number, delta: number) {
    const ids = active.map((w) => w.id);
    const j = index + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    run(() => api.put('/admin/workspaces/order', ids));
  }

  return (
    <>
      <div className="page-head"><h1>Setări</h1></div>
      <ThemePicker />
      <ErrorMsg error={error ?? actionError} />

      <section>
        <div className="section-head">
          <h2>Workspace-uri</h2>
          <button className="primary" onClick={() => setEditing('new')}>+ Workspace nou</button>
        </div>
        <p className="muted small">Ordinea de aici este ordinea din lista derulantă de sub siglă.</p>
        <table>
          <thead><tr><th>Ordine</th><th>Nume</th><th>Vizibilitate</th><th>Aplicații</th><th>Membri</th><th /></tr></thead>
          <tbody>
            {active.map((w, i) => (
              <tr key={w.id}>
                <td className="nowrap">
                  <button className="link" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Mută sus">▲</button>
                  <button className="link" disabled={i === active.length - 1} onClick={() => move(i, 1)} aria-label="Mută jos">▼</button>
                </td>
                <td><span className="dot" style={{ background: w.color ?? '#083050' }} /> <Link to={`/w/${w.id}`}>{w.name}</Link></td>
                <td>{PRIVACY[w.privacy]}</td>
                <td>{w.appCount}</td>
                <td>{w.memberCount}</td>
                <td className="right nowrap">
                  <button className="link" onClick={() => setEditing(w)}>Editează</button>
                  <button className="link danger" onClick={() => {
                    if (confirm(`Arhivați workspace-ul „${w.name}”? Datele se păstrează și îl puteți restaura.`)) {
                      run(() => api.del(`/workspaces/${w.id}`));
                    }
                  }}>Arhivează</button>
                  <button className="link danger" onClick={() => setDeleting(w)}>Șterge</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {archived.length > 0 && (
          <>
            <h2>Arhivate</h2>
            <table>
              <tbody>
                {archived.map((w) => (
                  <tr key={w.id} className="inactive">
                    <td>{w.name}</td>
                    <td>{w.appCount} aplicații</td>
                    <td className="right nowrap">
                      <button className="link" onClick={() => run(() => api.post(`/admin/workspaces/${w.id}/restore`, {}))}>Restaurează</button>
                      <button className="link danger" onClick={() => setDeleting(w)}>Șterge</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {deleting && (
        <DeleteWorkspace ws={deleting} onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); reload(); onChanged(); }} />
      )}
      {editing && (
        <WorkspaceForm initial={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); onChanged(); }} />
      )}
    </>
  );
}

/** Ștergere definitivă: cere scrierea numelui workspace-ului. */
function DeleteWorkspace({ ws, onClose, onDeleted }: { ws: AdminWorkspace; onClose: () => void; onDeleted: () => void }) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/workspaces/${ws.id}/delete`, { confirmName: typed });
      onDeleted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  return (
    <Modal title={`Ștergere definitivă: ${ws.name}`} onClose={onClose}>
      <p className="error">
        Se șterg definitiv workspace-ul, cele {ws.appCount} aplicații cu toate înregistrările, comentariile, sarcinile și
        membrii lui. Operația <strong>nu poate fi anulată</strong>. Dacă vreți doar să-l ascundeți, folosiți „Arhivează”.
      </p>
      <label>Scrieți numele workspace-ului pentru confirmare: <strong>{ws.name}</strong>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
      </label>
      <ErrorMsg error={error} />
      <div className="actions">
        <button onClick={onClose}>Renunță</button>
        <button className="primary" style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
          disabled={busy || typed.trim() !== ws.name} onClick={remove}>Șterge definitiv</button>
      </div>
    </Modal>
  );
}
